import { memo, useMemo, type CSSProperties } from 'react';
import { css, keyframes, Theme } from '@emotion/react';
import { CheckCircle, WarningCircle, Warning, CircleNotch, ToggleLeft, XCircle } from '@phosphor-icons/react';
import { Clone } from 'client/components/Icons/Clone';
import { Trash } from 'client/components/Icons/Trash';
import {
  FigureState,
  DeploymentCombinedState,
  StatusIcon,
  useStatusColorToHex,
  getFeaturesFromState,
} from 'client/components/Status/statusUtils';
import { Easing, borderRadius } from 'client/Theme';
import { Avatar, AvatarProps } from './Avatar';

export type { FigureState };

const stateIcons: Record<StatusIcon, JSX.Element | null> = {
  none: null,
  check: <CheckCircle />,
  warning: <Warning />,
  error: <WarningCircle />,
  fatal: <XCircle />,
  service: <XCircle />,
  loading: <CircleNotch />,
  toggle: <ToggleLeft />,
  delete: <Trash animate />,
  clone: <Clone animate />,
};

export interface ListItemFigureProps {
  image?: AvatarProps | null;
  icon?: JSX.Element | null;
  size?: number;
  ornament?: JSX.Element | null;
  state?: FigureState | DeploymentCombinedState | null;
  className?: string;
}

/**
 * Render an icon or an image depending on what is given as props
 */
export const ListItemFigure = memo(
  ({ image, icon: iconProp, size = 32, ornament = null, state: stateProp = null, ...other }: ListItemFigureProps) => {
    const stateFeatures = getFeaturesFromState(stateProp);
    const stateColors = useStatusColorToHex();

    const icon = stateIcons[stateFeatures.icon];
    const color = stateColors[stateFeatures.color];

    const isErrorPing = ['service', 'error', 'fatal'].includes(stateFeatures.icon);

    const stateIcon = useMemo(() => {
      const element = ornament || icon;
      if (!element) return null;
      const props = { ...element.props };
      const ornamentSize = ornament ? size - 10 : 22;

      return (
        <div
          className="ornament"
          css={[stateIconStyle, isErrorPing && stateIconErrorStyle, stateFeatures.icon === 'loading' && stateIconLoadingStyle]}
        >
          {/* To fill gaps in the background of icons with holes in them */}
          <element.type weight="regular" size={ornamentSize} color={color} {...props} />
          {/* To draw the outline of the icon */}
          <element.type weight="fill" size={ornamentSize} color={color} {...props} />
          {/* To draw the actual icon */}
          <element.type weight="fill" size={ornamentSize} color={color} {...props} />
        </div>
      );
    }, [icon, ornament, color, stateFeatures.icon, isErrorPing]);

    if (image) {
      return (
        <figure style={{ width: size, height: size }} css={figureStyle} {...other}>
          <Avatar {...image} size={size} />
          <div
            css={[imageStateBorderStyle, image.shape === 'hexagon' && imageStateHexagonBorderStyle]}
            style={
              {
                '--borderColor': ['warning', 'service', 'error', 'fatal'].includes(stateFeatures.icon) ? color : 'transparent',
              } as CSSProperties
            }
          >
            {image.shape === 'hexagon' && (
              <svg width="125" height="110" viewBox="0 0 125 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M122.5 49a12 12 0 0 1 0 12l-24.8 43a12 12 0 0 1-10.4 6H37.7a12 12 0 0 1-10.4-6L2.5 61a12 12 0 0 1 0-12L27.3 6a12 12 0 0 1 10.4-6h49.6a12 12 0 0 1 10.4 6l24.8 43Z"
                  stroke="#fff"
                />
              </svg>
            )}
          </div>
          {stateIcon}
        </figure>
      );
    }

    if (iconProp) {
      const props = { ...iconProp.props };
      if (stateFeatures.isEnabled) props.animate = stateFeatures.isEnabled;
      return (
        <figure css={figureStyle} style={{ width: size, height: size }} {...other}>
          <iconProp.type color={color} size={size} weight="duotone" {...props} />
          {stateIcon}
        </figure>
      );
    }

    return null;
  }
);

const pulseAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const stateIconStyle = (theme: Theme) => css`
  position: absolute;
  padding: 1px;
  right: -4px;
  bottom: -4px;

  box-sizing: border-box;

  svg {
    position: absolute;
    bottom: 0;
    right: 0;
  }

  svg:not(:last-of-type) {
    overflow: visible;
    color: ${theme.color.background};

    path {
      stroke: currentColor;
      fill: currentColor;
      stroke-width: 64px;
    }

    path,
    line,
    circle {
      fill: currentColor;
    }
  }
`;

const stateIconErrorStyle = (theme: Theme) => css`
  &::after {
    content: '';
    position: absolute;
    display: block;
    bottom: 0;
    right: 0;
    width: 22px;
    height: 22px;
    background-color: ${theme.color.red[100]};
    background: radial-gradient(transparent 10%, ${theme.color.red[100]} 100%);
    border-radius: 50%;

    animation: ${pulseAnimation} 2s infinite ${Easing.springy};
  }
`;

const stateIconLoadingStyle = (theme: Theme) => css`
  svg:first-of-type {
    background-color: ${theme.color.grey[60]};
    border-radius: 50%;

    path {
      display: none;
    }
  }

  svg:last-of-type {
    animation: ${spinAnimation} 1s infinite linear;
  }
`;

const imageStateBorderStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: ${borderRadius}px;
  border-color: var(--borderColor);

  > svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: visible;

    > path {
      stroke: var(--borderColor);
      stroke-width: 10px;
    }
  }
`;

const imageStateHexagonBorderStyle = css`
  border: none;
`;

const figureStyle = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
`;
